import _ from "lodash";
import React, { useMemo, useRef } from 'react';

import { useStoreState, useStoreActions } from "easy-peasy";
import { types } from "../lib/store";

import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import {useTranslation} from "react-i18next";

import { types as SimulationTypes } from "./Simulation";

export const CurveTypes = [
  { type: 'fan',                          abbr: "PS", dashStyle: '', visible: true, show_label: true, yAxis: 0, temperature: false, density: false },
  { type: 'dynamic_pressure',             abbr: "PD", dashStyle: 'dot', visible:  true, show_label: false, yAxis: 0, temperature: false, density: false },
  { type: 'total_pressure',               abbr: "PT", dashStyle: 'LongDash', visible:  true, show_label: false, yAxis: 0, temperature: false, density: false },
  { type: 'mechanical_power',             abbr: "CPM", dashStyle: '', visible:  true, show_label: false, yAxis: 1, temperature: false, density: false },
  { type: 'absorbed_power',               abbr: "PA", dashStyle: 'LongDash', visible:  false, show_label: false, yAxis: 1, temperature: false, density: true },
  { type: 'specific_consumption',         abbr: "SPF", dashStyle: 'dot', visible:  false, show_label: false, yAxis: 2, temperature: false, density: true },
  // { type: 'efficiency', dashStyle: '', visible:  false, show_label: false, yAxis: 3 }, // Old, not in use
  { type: 'mechanical_total_efficiency',  abbr: "MTE", dashStyle: '', visible:  true, show_label: false, yAxis: 3, temperature: false, density: false },
  { type: 'mechanical_static_efficiency', abbr: "MEE", dashStyle: 'LongDash', visible:  false, show_label: false, yAxis: 3, temperature: false, density: false },
  { type: 'fan_static_efficiency',        abbr: "FEE", dashStyle: 'Dot', visible:  false, show_label: false, yAxis: 3, temperature: false, density: true },
  { type: 'fan_total_efficiency',         abbr: "FTE", dashStyle: 'DotDash', visible:  false, show_label: false, yAxis: 3, temperature: false, density: true },
];

export const DefaultActiveSeries = ['fan', 'dynamic_pressure', 'total_pressure', 'area_no_recomenada'];

function Graph() {
  const { t } = useTranslation();

  const activeSeries = useRef(DefaultActiveSeries);

  const fan = useStoreState(state => state.selection.fan);
  const graphData = useStoreState(state => state.selection.graph);
  const designPoint = useStoreState(state => state.selection.designPoint);
  const focusedPair = useStoreState(state => state.selection.focusedPair);
  const focusingCurve = useStoreState(state => state.selection[types.IS_FOCUSING_CURVE]);
  const hasDesignPoint = useStoreState(state => state.selection[types.HAS_DESIGN_POINT]);
  const servicePoint = useStoreState(state => state.selection.servicePoint);
  const powerLine = useStoreState(state => state.selection.customizations.powerLine);
  const showAlternativePoint = useStoreState(state => state.selection.showAlternativePoint);
  const simulationType = useStoreState(state => state.selection.customizations.simulationType);
  const enabledFeatures = useStoreState(state => state.selection.features);
  const units = useStoreState(state => state.selection.units);

  const updateActiveSeries = useStoreActions(actions => actions.selection[types.SET_ACTIVE_SERIES]);
  const updateStorePoint = useStoreActions(actions => actions.selection[types.SET_DESIGN_POINT]);
  // const loadGraph = useStoreActions(actions => actions.selection[types.LOAD_GRAPH]);

  const isVSDFrequencySelected = (simulationType === SimulationTypes.FREQUENCY && designPoint.frequency !== fan.frequency);

  // useEffect(() => {
  //   loadGraph();
  // }, [designPoint]);

  const SeriesNames = {
    fan: t('frontend:user_preferences.pressure'),
    dynamic_pressure: t('frontend:fan.dynamic_pressure'),
    total_pressure: t('frontend:fan.total_pressure'),
    absorbed_power: t('frontend:fan.absorbed_power'),
    specific_consumption: t('frontend:fan.specific_consumption'),
    mechanical_power: t('frontend:fan.mechanical_power'),
    mechanical_static_efficiency: t('frontend:fan.mechanical_static_efficiency'),
    mechanical_total_efficiency: t('frontend:fan.mechanical_total_efficiency'),
    fan_static_efficiency: t('frontend:fan.fan_static_efficiency'),
    fan_total_efficiency: t('frontend:fan.fan_total_efficiency'),
    area_no_recomenada: 'area_no_recomenada',
    resistive: t('frontend:fan.resistive'),
  };

  const getColor = (curve_name, opacity) => {
    opacity = opacity || 1;

    switch(curve_name){
      case 'fan':
        return 'rgba(0, 85, 175,' + opacity + ')';
      case 'dynamic_pressure':
        return 'rgba(0, 85, 175,' + opacity + ')';
      case 'total_pressure':
        return 'rgba(0, 85, 175,' + opacity + ')';
      case 'absorbed_power':
        case 'mechanical_power':
        case 'specific_consumption':
        return 'rgba(208, 0, 0,' + opacity + ')';
      // case 'efficiency': // Old, not in use
      case 'mechanical_static_efficiency':
        case 'mechanical_total_efficiency':
        case 'fan_static_efficiency':
        case 'fan_total_efficiency':
        return 'rgba(38, 137, 43,' + opacity + ')';
      case 'acoustic' :
        return 'rgba(0, 65, 200,' + opacity + ')';
      default:
        return 'rgba(0, 0, 0,' + opacity + ')';
    }
  };

  const getDefaultConfig = useMemo(() => {
    const { density } = graphData;

    return {
      chart: {
        renderTo: 'fan-graph',
        type: 'spline',
        inverted: false,
        backgroundColor: 'none',
        height: 650,
        events: {
          click: function (e) {
            if (enabledFeatures.includes("controls")) {
              updateStorePoint({
                ...designPoint,
                flow: _.round(e.xAxis[0].value, 2),
                pressure: _.round(e.yAxis[0].value, 2)
              });
            }
          }
        }
      },
      exporting: {
        enabled: false,
        sourceWidth: 1500,
        sourceHeight: 900
      },
      title: {
        text: fan.name
      },
      subtitle: {
        text: `${designPoint.temperature} ${t(`units:${units.temperature}`)} - ${designPoint.height} ${t(`units:${units.distance}`)} - ${density} Kg/m³`,
      },
      tooltip: {
        enabled: false
      },
      credits: {
        enabled: false
      },
      formAttributes: {
        encoding: 'UTF-8'
      },
      xAxis: {
        endOnTick: true,
        min: 0,
        maxPadding: 0,
        gridLineColor: '#cdcdcd',
        gridLineDashStyle: 'dash',
        gridLineWidth: '1',
        title: {
          enabled: true,
          text: `${t('frontend:fan.flow')} (${t(`units:${units.flow}`)})`,
          useHTML: true
        },
        labels: {
          formatter: function () {
            return this.value;
          }
        },
        showLastLabel: true
      },
      yAxis: [
        {
          maxPadding: 0.05,
          endOnTick: true,
          alignTicks: false,
          gridLineColor: '#cdcdcd',
          gridLineDashStyle: 'dash',
          min: 0,
          title: {
            text: `${t('frontend:fan.pressure')} (${t(`units:${units.pressure}`)})`,
            useHTML: true,
            style: {
              color: getColor('fan')
            }
          },
          labels: {
            formatter: function () {
              return this.value + '';
            },
            style: {
              color: getColor('fan')
            }
          }
        },
        {
          endOnTick: false,
          alignTicks: false,
          gridLineColor: '#cdcdcd',
          gridLineDashStyle: 'dash',
          showEmpty: false,
          min: 0,
          title: {
            text: `${t('frontend:fan.power')} (kW)`,
            style: {
              color: getColor('absorbed_power')
            }
          },
          labels: {
            style: {
              color: getColor('absorbed_power')
            }
          },
          lineWidth: 0,
          opposite: true,
          events: {
            afterSetExtremes: function (event) {
              // TODO:
              //if (self.Graph.updatingCustomPower) {
              //  return;
              //}

              // TODO:
              //self.Graph.updateCustomPower();
            }
          }
        },
        {
          endOnTick: false,
          alignTicks: false,
          gridLineColor: '#cdcdcd',
          gridLineDashStyle: 'dash',
          showEmpty: false,
          min: 0,
          title: {
            text: `${t('frontend:fan.specific_consumption')} (W/l/s)`,
            style: {
              color: getColor('specific_consumption')
            }
          },
          labels: {
            style: {
              color: getColor('specific_consumption')
            }
          },
          lineWidth: 0,
          opposite: true
        },
        {
          endOnTick: false,
          alignTicks: false,
          gridLineColor: '#cdcdcd',
          gridLineDashStyle: 'dash',
          showEmpty: false,
          min: 0,
          title: {
            text: `${t('frontend:fan.efficiency')} (%)`,
            style: {
              color: getColor('mechanical_static_efficiency')
            }
          },
          labels: {
            style: {
              color: getColor('mechanical_static_efficiency')
            }
          },
          lineWidth: 0,
          opposite: true
        }
      ],
      legend: {
        itemStyle: {
          color: '#000000',
          fontWeight: 'normal'
        },
        borderWidth: 0,
        symbolPadding: 4,
        symbolWidth: 30
      },
      plotOptions: {
        series: {
          events: {
            legendItemClick: function (event) {
              // Add or remove the series from the activeSeries array so we can
              // track the series that the user want to see in the screen
              // the curve also includes the index
              const seriesId = event.target.userOptions.id.replace(/_\d+[a-zA-Z]?$/, "");
              //series in chart
              //event.target.chart.options.series.map(x => x.id);
              //if element is not visible, set to visible
              if(!this.visible) {
                activeSeries.current = _.xor(activeSeries.current, [seriesId]);
              } else { //if element is visible, set to not visible
                activeSeries.current = activeSeries.current.filter(item => item !== seriesId)
              }
              updateActiveSeries(activeSeries.current);
            },
            // show: function (event) {
            //   // Try to move this to another event in a future version,
            //   // because this is fired for each curve in the series
            //   if (event.target.userOptions.yAxis === 1) {
            //     self.Graph.updateCustomPower();
            //   }
            // },
            // hide: function (event) {
            //   // Try to move this to another event in a future version,
            //   // because this is fired for each curve in the series
            //   if (event.target.userOptions.yAxis === 1) {
            //     self.Graph.removeCustomPower();
            //   }
            // }
          },
          states: {
            inactive: {
              opacity: 1
            }
          }
        },
        spline: {
          enableMouseTracking: false,
          turboThreshold: 0,
          stickyTracking: false,
          allowPointSelect: false,
          lineWidth: 1.5,
          animation: false,
          marker: {
            enabled: false
          }
        },
        areaspline: {
          turboThreshold: 0,
          enableMouseTracking: false,
          fillOpacity: 0.1,
          lineWidth: 0,
          stickyTracking: false,
          allowPointSelect: false,
          animation: false,
          marker: {
            enabled: false
          }
        },
        tooltip: {
          enabled: false
        }
      },
      series: []
    }
  }, [updateStorePoint, designPoint, fan, graphData, t]);

  const getSeriesData = (curvePoints, curveType, curveName, curveOpacity, curveID, linkedTo) => {
    let legend = SeriesNames[curveType.type];

    if (curveType.temperature && designPoint.temperature !== 20) {
      legend = legend + ' (20ºC)';
    }

    if (curveType.density && designPoint.temperature !== 20) {
      legend = legend + ' (1.2 Kg/m³)';
    }

    let dataset = [];

    if (curveType.show_label || isVSDFrequencySelected) {
      dataset.push({
        id: 'label' + curveID,
        name: 'label' + curveID,
        x: curvePoints[0][0],
        y: curvePoints[0][1],
        dataLabels: {
          enabled: true,
          format: String(curveName),
          color: getColor("annotation", curveOpacity),
          align: 'right',
          style: {
            fontSize: '9px'
          },
          y: 9
        }
      });
    }

    let series = {
      id: curveType.type + '_' + curveID,
      name: legend,
      color: getColor(curveType.type, curveOpacity),
      visible: activeSeries.current.indexOf(curveType.type) !== -1 || (focusingCurve && curveType.type === "mechanical_power"),
      dashStyle: curveType.dashStyle,
      yAxis: curveType.yAxis,
      data: dataset.concat(curvePoints),
    };

    if (curveID !== linkedTo) {
      series.linkedTo = curveType.type + '_' + linkedTo;
    }

    return series;
  };

  const getCurveName = function(curve, isCalculatedCurve) {
    // set default curve name
    let name = curve.name;
    //add suffix to curve name if vsd active
    if(isVSDFrequencySelected) {
      if(curve.name !== ' ') {
        name += ' - ';
      }
      name += curve.frequency + ' Hz';
      if(isCalculatedCurve) {
        name += ' VSD';
      }
    }
    return name;
  }

  const chartOptions = useMemo(() => {
    const config = { ...getDefaultConfig, series: [] };

    if (_.isEmpty(graphData)) {
      return config;
    }

    const { curves } = graphData;
    const { resistive_curve, service_points, alternative_service_points, info } = servicePoint;

    const forceHighlight = service_points === null || (!hasDesignPoint && !focusingCurve);
    const lowOpacityValue =  0.20;

    _.each(curves, (curve) => {
      let curveOpacity = 1;
      let linkedTo = curves[0].id;
      let isCalculatedCurve = (curve.frequency !== fan.frequency);
      if (!forceHighlight) {
        if (focusingCurve) {
          const focusedCurveId = curves.find(curve => curve.pair_identifier === focusedPair).id;

          curveOpacity = focusedPair === curve.pair_identifier ? 1 : lowOpacityValue;

          if (isVSDFrequencySelected) {
            linkedTo = `${focusedCurveId}c`;
          } else {
            linkedTo = focusedCurveId;
          }
        } else {
          const curveIds = showAlternativePoint
            ? (info['alternative_curve_ids'] || [])
            : (info['recommended_curve_ids'] || []);

          if (isVSDFrequencySelected) {
            linkedTo = `${curveIds[0]}c`;
            if(service_points === null) {
              curveOpacity = isCalculatedCurve ? 1 : lowOpacityValue;
            } else if (service_points.find((item) => parseInt(item.curve_id) === parseInt(curve.id))) {
              curveOpacity = isCalculatedCurve ? 1 : lowOpacityValue;
            } else {
              curveOpacity = lowOpacityValue;
            }
          } else {
            curveOpacity = curveIds.includes(curve.id) ? 1 : lowOpacityValue;
            linkedTo = curveIds[0];
          }
        }
      //highlight calculated curve when not SP is selected (VSD active and selected freq.)
      } else if(!service_points && !hasDesignPoint && !focusingCurve && isVSDFrequencySelected) {
        curveOpacity = isCalculatedCurve ? 1 : lowOpacityValue;
        linkedTo = `${linkedTo}c`;
      }

      _.each(CurveTypes, (curve_type) => {
        let curvePoints = curve[curve_type.type];

        // check extra curves that are only available when service points are present
        if (curvePoints === null) {
          (service_points || []).forEach((servicePoint, index) => {
            if (servicePoint === null || servicePoint.curve_id !== curve.id)
              return true;

            curvePoints = (servicePoint.curves || {})[curve_type.type];
          });

          (alternative_service_points || []).forEach((servicePoint, index) => {
            if (servicePoint === null || servicePoint.curve_id !== curve.id)
              return true;

            curvePoints = (servicePoint.curves || {})[curve_type.type];
          });
        }

        if (curvePoints === null)
          return true;

        const curveID = isCalculatedCurve ? `${curve.id}c` : curve.id;
        const curveName = getCurveName(curve, isCalculatedCurve);
        const series = getSeriesData(curvePoints, curve_type, curveName, curveOpacity, curveID, linkedTo);

        config.series.push(series);
      });

      //fix for active series when set a VSD frequency
      //case not available series is stills showing when selected a vsd frequency (github issue: #1078)
      let activeSeriesToSet = [];
      //get all available series on graph
      const seriesIds = config.series.map(x => x.id.replace(/_\d+[a-zA-Z]?$/, ""));
      //for each series visible in graph...
      for(let i = 0; i < activeSeries.current.length; i++) {
        //if active series exists ins series ids, save to available series visibility
        if(seriesIds.indexOf(activeSeries.current[i]) > -1 && !activeSeriesToSet.includes(activeSeries.current[i])) {
          //new array on save the active series is this is available on series graph
          activeSeriesToSet.push(activeSeries.current[i]);
        }
      } //update active series with only available series
      activeSeries.current = activeSeriesToSet;
      updateActiveSeries(activeSeriesToSet);
    });

    if (resistive_curve) {
      let dataset = _.map(resistive_curve, (item) => ({ x: item[0], y: item[1] }));

      dataset.splice(
        _.sortedIndexBy(dataset, { x: designPoint.flow }, (item) => { return item['x']; }), 0,
        {
          id: 'selection_point',
          name: 'selection_point',
          x: designPoint.flow,
          y: designPoint.pressure,
          color: "rgba(0, 0, 0, 0.8)",
          marker: {
            enabled: true,
            radius: 4,
            symbol: 'circle'
          }
        },
      );

      if (service_points) {
        _.each(service_points, (servicePoint, index) => {
          if (servicePoint === null)
            return true;

          const curveOpacity = (isVSDFrequencySelected && servicePoint.frequency === fan.frequency) ? 0.50 : 1;

          // Add the service point to the graph in the position to keep it ordered
          dataset.splice(
            _.sortedIndexBy(dataset, { x: servicePoint['flow'] }, (item) => { return item['x']; }), 0,
            {
              id: 'recommended_point_' + servicePoint.curve_id,
              name: 'recommended_point_' + servicePoint.curve_id,
              x: servicePoint['flow'],
              y: servicePoint['static_pressure'],
              color: `rgba(0, 85, 175, ${curveOpacity})`,
              marker: {
                enabled: true,
                radius: 4,
                symbol: 'circle'
              }
            },
          );
        });
      }

      if (alternative_service_points) {
        _.each(alternative_service_points, (servicePoint, index) => {
          if (servicePoint === null)
            return true;

          const curveOpacity = (isVSDFrequencySelected && servicePoint.frequency === fan.frequency) ? 0.50 : 1;

          dataset.splice(
            _.sortedIndexBy(dataset, { x: servicePoint['flow'] }, (item) => { return item['x']; }), 0,
            {
              id: 'alternative_point_' + servicePoint.curve_id,
              name: 'alternative_point_' + servicePoint.curve_id,
              x: servicePoint['flow'],
              y: servicePoint['static_pressure'],
              color: `rgba(165, 165, 165, ${curveOpacity})`,
              marker: {
                enabled: true,
                radius: 4,
                symbol: 'circle'
              }
            },
          );
        });
      }

      config.series.push({
        id: 'resistive',
        name: SeriesNames['resistive'],
        color: getColor('resistive'),
        marker: {
          enabled: false
        },
        dashStyle: 'Solid',
        shadow: false,
        animation: false,
        enableMouseTracking: false,
        zIndex: 99,
        data: dataset
      })
    }

    if (powerLine.power && config.yAxis[1]) {
      config.yAxis[1].plotLines = [
        {
          id: "customPower",
          value: powerLine.power,
          color: getColor("mechanical_power"),
          dashStyle: 'shortdash',
          width: 2,
          zIndex: 99,
          label: {
            text: "" + powerLine.power + " kW",
            style: { color: getColor("mechanical_power") },
            align: "right",
            textAlign: "right",
            x: -5
          }
        }
      ];

      // If yAxis.max is undefined, this returns false, and it is ok,
      // because if we set the extreme, it draws the yAxis
/*      debugger;
      if (parseFloat(powerLine) > config.yAxis[1].max && config.yAxis[1].visible) {
        console.log("seto max y");*/
        config.yAxis[1].softMax = powerLine.power * 1.05;
      /*}*/
    } else {
      if (config.yAxis[1]) {
        _.remove(config.yAxis[1].plotLines || [], function (plotLine) {
          return plotLine.id === "customPower";
        });

        config.yAxis[1].softMax = undefined;
      }
    }

    return config;
  }, [graphData, getDefaultConfig, servicePoint, designPoint.flow, designPoint.pressure, designPoint.temperature, powerLine, showAlternativePoint, SeriesNames]);

  const chartCallbackFunction = (chart) => {
    if (_.isEmpty(graphData)) {
      chart.showLoading();
    } else {
      chart.hideLoading();
    }
  };

  return (
    <>
      { <HighchartsReact highcharts={Highcharts} options={chartOptions} callback={chartCallbackFunction} immutable={true} /> }
    </>
  );
}

export default Graph;
